import Encounter from "@/encounter/model/Encounter.ts";
import {http} from "@/api/HttpClient.ts";
import Combatant from "@/encounter/model/Combatant.ts";
import {toCombatantData, type CombatantData} from "@/encounter/model/CombatantData.ts";
import type {Character} from "@/characters/model/Character.ts";
import CombatantTransform from "@/encounter/model/CombatantTransform.ts";

export type CombatantTransformBatch = {
    interactionId: string;
    sequence: number;
    transforms: CombatantTransform[];
};

export type CombatantTransformEvent = CombatantTransformBatch & {
    final: boolean;
};

export type CombatantSnapshot = {
    combatants: CombatantData[];
    characters: Character[];
};

export type CombatantEvent = {
    upserted?: CombatantData[];
    characters?: Character[];
    removedIds?: string[];
    transformed?: CombatantTransformEvent;
};

type CombatantUpdate = Pick<Combatant,
    "id" | "name" | "currentLife" | "maxLife" | "hostile" |
    "notes" | "position" | "rotation" | "hidden" | "levelId" | "tokenFrame"
>;

const toCombatantUpdate = (combatant: Combatant): CombatantUpdate => ({
    id: combatant.id,
    name: combatant.name,
    currentLife: combatant.currentLife,
    maxLife: combatant.maxLife,
    hostile: combatant.hostile,
    notes: combatant.notes,
    position: combatant.position,
    rotation: combatant.rotation,
    hidden: combatant.hidden,
    levelId: combatant.levelId,
    tokenFrame: combatant.tokenFrame,
});

export async function getCombatants(encounterId: string): Promise<CombatantSnapshot> {
    const res = await http.get<CombatantSnapshot>(`/encounters/${encounterId}/combatants`);
    return res.data;
}

export async function saveCombatant(encounter: Encounter, combatant: Combatant): Promise<CombatantEvent> {
    const response = await http.put<CombatantEvent>(`/encounters/${encounter.id}/combatants`, toCombatantData(combatant));
    return response.data;
}

export async function addCharacterToEncounter(encounterId: string, characterId: string): Promise<CombatantEvent> {
    const response = await http.post<CombatantEvent>(
        `/encounters/${encounterId}/combatants/from-character`,
        {characterId},
    );
    return response.data;
}

export async function saveCombatants(encounter: Encounter, combatants: Combatant[]): Promise<CombatantData[]> {
    const response = await http.post<CombatantData[]>(
        `/encounters/${encounter.id}/combatants`,
        combatants.map(toCombatantUpdate),
    );
    return response.data;
}

export async function deleteCombatants(encounter: Encounter, combatants: Combatant[]): Promise<string[]> {
    const response = await http.delete<string[]>(`/encounters/${encounter.id}/combatants`, {
        data: combatants.map(combatant => combatant.id),
    });
    return response.data;
}

export async function saveCombatantTransforms(
    encounter: Encounter,
    batch: CombatantTransformBatch,
): Promise<CombatantTransformEvent> {
    const response = await http.patch<CombatantTransformEvent>(
        `/encounters/${encounter.id}/combatants/transforms`,
        batch,
    );
    return response.data;
}
